import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/admin/ProtectedRoute";

import Header from "./components/Header";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import SimplePage from "./pages/SimplePage";
import AboutPage from "./pages/AboutPage";
import BoardMembersPage from "./pages/BoardMembersPage";
import ContactPage from "./pages/ContactPage";
import ProjectsPage from "./pages/projects/ProjectsPage";
import ProjectDetailsPage from "./pages/projects/ProjectDetailsPage";
import ComplaintPage from "./pages/ComplaintPage";
import ComplaintDataBankPage from "./pages/ComplaintDataBankPage";
import PartnershipPage from "./pages/PartnershipPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import DisclaimerPage from "./pages/DisclaimerPage";

import BlogPage from "./pages/blog/BlogPage";
import BlogSinglePage from "./pages/blog/BlogSinglePage";

import ImageGalleryPage from "./pages/media/ImageGalleryPage";
import VideoGalleryPage from "./pages/media/VideoGalleryPage";

import AdminSigninPage from "./pages/admin/AdminSigninPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";

import AdminPosts from "./pages/admin/posts/AdminPosts";
import AdminAddPost from "./pages/admin/posts/AdminAddPost";
import AdminEditPost from "./pages/admin/posts/AdminEditPost";

import AdminCategories from "./pages/admin/categories/AdminCategories";
import AdminTags from "./pages/admin/tags/AdminTags";
import AdminComments from "./pages/admin/comments/AdminComments";
import AdminMedia from "./pages/admin/media/AdminMedia";
import AdminUsers from "./pages/admin/users/AdminUsers";
import AdminNewsletter from "./pages/admin/newsletter/AdminNewsletter";
import AdminContacts from "./pages/admin/contacts/AdminContacts";
import AdminSEOSettings from "./pages/admin/seo/AdminSEOSettings";
import AdminProjects from "./pages/admin/projects/AdminProjects";
import AdminAddProject from "./pages/admin/projects/AdminAddProject";
import AdminEditProject from "./pages/admin/projects/AdminEditProject";
import AdminProjectCategories from "./pages/admin/projects/AdminProjectCategories";
import AdminComplaints from "./pages/admin/complaints/AdminComplaints";
import AdminComplaintDetails from "./pages/admin/complaints/AdminComplaintDetails";
import AdminPartnerships from "./pages/admin/partnerships/AdminPartnerships";
import AdminGallery from "./pages/admin/gallery/AdminGallery";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const WebsiteLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              borderRadius: "14px",
              fontWeight: "600",
            },
          }}
        />

        <Routes>
          <Route
            path="/"
            element={
              <WebsiteLayout>
                <Home />
              </WebsiteLayout>
            }
          />

          <Route
            path="/about"
            element={
              <WebsiteLayout>
                <AboutPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/board-members"
            element={
              <WebsiteLayout>
                <BoardMembersPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/departments"
            element={
              <WebsiteLayout>
                <SimplePage
                  title="Departments"
                  subtitle="Meet the directorates and units that power the Akwa Ibom State Universal Education Board mandate across Akwa Ibom State."
                />
              </WebsiteLayout>
            }
          />

          <Route
            path="/programs"
            element={
              <WebsiteLayout>
                <SimplePage
                  title="Programs"
                  subtitle="Explore programs improving basic education across Akwa Ibom State."
                />
              </WebsiteLayout>
            }
          />

          <Route
            path="/recruitment"
            element={
              <WebsiteLayout>
                <SimplePage
                  title="Recruitment"
                  subtitle="Recruitment opportunities, application guidelines, and official employment updates."
                />
              </WebsiteLayout>
            }
          />

          <Route
            path="/privacy"
            element={
              <WebsiteLayout>
                <PrivacyPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/terms"
            element={
              <WebsiteLayout>
                <TermsPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/disclaimer"
            element={
              <WebsiteLayout>
                <DisclaimerPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/projects"
            element={
              <WebsiteLayout>
                <ProjectsPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/projects/:slug"
            element={
              <WebsiteLayout>
                <ProjectDetailsPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/complaints"
            element={
              <WebsiteLayout>
                <ComplaintPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/complaints-data-bank"
            element={
              <WebsiteLayout>
                <ComplaintDataBankPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/partnership"
            element={
              <WebsiteLayout>
                <PartnershipPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/media"
            element={
              <WebsiteLayout>
                <SimplePage
                  title="Media"
                  subtitle="News, press releases, photos, and videos."
                />
              </WebsiteLayout>
            }
          />

          <Route
            path="/media/images"
            element={
              <WebsiteLayout>
                <ImageGalleryPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/media/videos"
            element={
              <WebsiteLayout>
                <VideoGalleryPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/contact"
            element={
              <WebsiteLayout>
                <ContactPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/blog"
            element={
              <WebsiteLayout>
                <BlogPage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/blog/:slug"
            element={
              <WebsiteLayout>
                <BlogSinglePage />
              </WebsiteLayout>
            }
          />

          <Route
            path="/staff-mail"
            element={
              <WebsiteLayout>
                <SimplePage
                  title="Staff Mail"
                  subtitle="Official staff mail access for Akwa Ibom State Universal Education Board personnel."
                />
              </WebsiteLayout>
            }
          />

          <Route path="/admin-signin" element={<AdminSigninPage />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />

            <Route path="posts" element={<AdminPosts />} />
            <Route path="posts/add" element={<AdminAddPost />} />
            <Route path="posts/edit/:id" element={<AdminEditPost />} />

            <Route path="categories" element={<AdminCategories />} />
            <Route path="tags" element={<AdminTags />} />
            <Route path="comments" element={<AdminComments />} />
            <Route path="media" element={<AdminMedia />} />
            <Route path="gallery" element={<AdminGallery />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="seo" element={<AdminSEOSettings />} />

            <Route path="projects" element={<AdminProjects />} />
            <Route path="projects/add" element={<AdminAddProject />} />
            <Route path="projects/edit/:id" element={<AdminEditProject />} />
            <Route
              path="projects/categories"
              element={<AdminProjectCategories />}
            />

            <Route path="complaints" element={<AdminComplaints />} />
            <Route path="complaints/:id" element={<AdminComplaintDetails />} />

            <Route path="partnerships" element={<AdminPartnerships />} />
          </Route>

          <Route
            path="*"
            element={
              <WebsiteLayout>
                <SimplePage
                  title="Page Not Found"
                  subtitle="The page you are looking for does not exist."
                />
              </WebsiteLayout>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;